import { api } from "./api";

/**
 * Grabar con el micrófono y reproducir la respuesta.
 *
 * Todo lo que toca el navegador vive aquí, separado de los componentes, porque
 * son las dos cosas que más se rompen entre navegadores y conviene tener un
 * solo sitio donde mirar cuando falle en el móvil y no en el portátil.
 */

/** Qué formato de audio sabe grabar este navegador. */
function formatoSoportado(): string {
  // Safari solo graba mp4; Chrome y Firefox prefieren webm con opus. Se
  // prueba en orden y se coge el primero que acepte: si se fija uno a mano,
  // el iPhone se queda sin micrófono y no dice por qué.
  const candidatos = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const tipo of candidatos) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(tipo)) {
      return tipo;
    }
  }
  return "";
}

export function sePuedeGrabar(): boolean {
  return (
    typeof MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

export interface Grabacion {
  /** Corta, suelta el micrófono y devuelve lo grabado. */
  parar(): Promise<Blob>;
  /** Corta y tira la grabación: para cuando te arrepientes. */
  cancelar(): void;
  /** Milisegundos grabados de verdad, contados desde que el micrófono abrió. */
  duracionMs(): number;
  /** Volumen de ahora mismo, de 0 a 1. Para pintar la barra al hablar. */
  nivel(): number;
  /** El más alto de toda la grabación. Cero significa que no entró sonido. */
  nivelMaximo(): number;
}

/**
 * Empieza a grabar.
 *
 * El permiso del micrófono lo pide el navegador la primera vez. Si lo
 * deniegan, esto lanza — y quien llama lo cuenta, en vez de dejar un botón
 * pulsado que no hace nada.
 */
export async function grabar(): Promise<Grabacion> {
  const flujo = await navigator.mediaDevices.getUserMedia({
    audio: {
      // Una nota de voz se graba a menudo en la calle o en el coche.
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  const tipo = formatoSoportado();
  const grabadora = new MediaRecorder(flujo, tipo ? { mimeType: tipo } : undefined);
  const trozos: BlobPart[] = [];

  grabadora.ondataavailable = (e) => {
    if (e.data.size > 0) trozos.push(e.data);
  };

  /**
   * Se pide un trozo cada 200 ms en vez de uno solo al final.
   *
   * Sin esto, una nota corta podía llegar vacía: algunos navegadores no han
   * entregado nada todavía cuando se llama a `stop()`, y lo que se enviaba
   * eran cuatro bytes de cabecera. Con trozos, lo grabado ya está guardado
   * antes de soltar.
   */
  grabadora.start(200);
  const abiertoEn = Date.now();

  /**
   * Se mide el volumen que entra, además de grabarlo.
   *
   * Sin esto, una grabación muda y una que el transcriptor no entiende son
   * exactamente el mismo mensaje de error, y llevan a buscar en sitios
   * distintos: la primera es el micrófono del aparato, la segunda es OpenAI.
   * Y de paso sirve para pintar una barra que se mueve al hablar, que es la
   * única forma de saber que te está oyendo antes de soltar.
   */
  let nivelActual = 0;
  let nivelMaximo = 0;
  let midiendo = true;

  const audio = new AudioContext();
  const analizador = audio.createAnalyser();
  analizador.fftSize = 512;
  audio.createMediaStreamSource(flujo).connect(analizador);
  const muestras = new Uint8Array(analizador.frequencyBinCount);

  const medir = () => {
    if (!midiendo) return;
    analizador.getByteTimeDomainData(muestras);
    let pico = 0;
    for (const v of muestras) pico = Math.max(pico, Math.abs(v - 128) / 128);
    nivelActual = pico;
    if (pico > nivelMaximo) nivelMaximo = pico;
    requestAnimationFrame(medir);
  };
  requestAnimationFrame(medir);

  const soltarMicrofono = () => {
    midiendo = false;
    void audio.close().catch(() => {});
    flujo.getTracks().forEach((t) => t.stop());
  };

  return {
    duracionMs: () => Date.now() - abiertoEn,
    nivel: () => nivelActual,
    nivelMaximo: () => nivelMaximo,
    parar: () =>
      new Promise<Blob>((resolver) => {
        grabadora.onstop = () => {
          soltarMicrofono();
          resolver(new Blob(trozos, { type: grabadora.mimeType || "audio/webm" }));
        };
        grabadora.stop();
      }),
    cancelar: () => {
      grabadora.onstop = soltarMicrofono;
      // Un micrófono que se queda abierto enciende el punto rojo del móvil y
      // no se apaga hasta recargar. Se suelta siempre.
      try {
        grabadora.stop();
      } catch {
        soltarMicrofono();
      }
    },
  };
}

/** El audio que se está oyendo ahora mismo, para poder callarlo. */
let sonando: HTMLAudioElement | null = null;
/** Lo que se está generando ahora mismo, para poder cortarlo. */
let generando: AbortController | null = null;
/**
 * Qué lectura es la buena.
 *
 * Se lee a trozos, así que al callar hay peticiones y reproducciones en vuelo
 * que van a terminar igualmente. Sin un número que las invalide, la respuesta
 * vieja seguiría hablando por encima de la nueva.
 */
let lectura = 0;

export function callar(): void {
  lectura++;
  if (sonando) {
    sonando.pause();
    sonando.src = "";
    sonando = null;
  }
  if (generando) {
    generando.abort();
    generando = null;
  }
  if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
}

/**
 * Parte el texto en trozos que se puedan generar por separado.
 *
 * El primero corto y los siguientes largos, y eso es todo el truco: lo que se
 * tarda en empezar a hablar es lo que tarda OpenAI en generar el PRIMER trozo,
 * y generar cien caracteres es casi instantáneo mientras que generar dos mil
 * son varios segundos mirando una pantalla quieta.
 *
 * Se corta siempre en final de frase. Cortar a mitad se nota muchísimo: la
 * entonación se queda colgada y suena a corte de emisión.
 */
export function trocear(texto: string, primero = 160, resto = 700): string[] {
  const frases = texto.match(/[^.!?…]+(?:[.!?…]+|$)/g) ?? [texto];
  const trozos: string[] = [];
  let actual = "";

  for (const frase of frases) {
    const tope = trozos.length === 0 ? primero : resto;
    if (actual && actual.length + frase.length > tope) {
      trozos.push(actual.trim());
      actual = "";
    }
    actual += frase;
  }
  if (actual.trim()) trozos.push(actual.trim());
  return trozos;
}

/** Reproduce un audio y espera a que termine. */
function reproducir(audio: Blob): Promise<void> {
  return new Promise((listo, falla) => {
    const url = URL.createObjectURL(audio);
    const elemento = new Audio(url);
    sonando = elemento;
    const acabar = () => {
      URL.revokeObjectURL(url);
      listo();
    };
    elemento.onended = acabar;
    // Un error a mitad no puede dejar la promesa colgada para siempre: el resto
    // de trozos no llegaría a sonar nunca y no se sabría por qué.
    elemento.onerror = acabar;
    elemento.play().catch(falla);
  });
}

/**
 * Una voz que va diciendo lo que se le echa, sin esperar a tenerlo todo.
 *
 * Existe por la latencia, que es lo que decide si una conversación parece una
 * conversación. Con `leer()` hay que esperar a que el modelo termine de
 * escribir para empezar a generar el audio, y sólo entonces empieza a sonar:
 * dos esperas, una detrás de otra.
 *
 * Aquí se le va echando frase a frase MIENTRAS el modelo escribe. La primera
 * empieza a generarse cuando el modelo lleva una línea, no cuando lleva quince.
 * Las siguientes se generan mientras suena la anterior, así que a partir de la
 * primera no hay silencios.
 *
 * Se encadenan con promesas y no con una lista y un temporizador: cada trozo
 * espera al anterior por construcción, y no hay forma de que dos suenen a la
 * vez aunque el segundo se genere antes.
 */
export function vozEnCola() {
  let cadena: Promise<void> = Promise.resolve();
  let cortada = false;
  const mia = ++lectura;

  return {
    decir(texto: string): void {
      const limpio = texto.trim();
      if (!limpio || cortada) return;
      cadena = cadena.then(async () => {
        if (cortada || mia !== lectura) return;
        try {
          const audio = await api.leerEnVoz(limpio);
          if (!audio || audio.size === 0 || cortada || mia !== lectura) return;
          await reproducir(audio);
        } catch {
          /* si falla un trozo, los siguientes siguen: mejor entrecortado que mudo */
        }
      });
    },

    /** Espera a que termine de decir todo lo que se le ha echado. */
    async fin(): Promise<void> {
      await cadena;
    },

    cortar(): void {
      cortada = true;
      callar();
    },
  };
}

/**
 * Lee un texto en voz alta.
 *
 * Primero intenta la voz buena, la del servidor. Si no hay clave de OpenAI o
 * falla, recurre a la que trae el navegador: suena a GPS, pero es gratis,
 * instantánea y funciona sin conexión. Peor voz es mejor que ninguna.
 *
 * **Se lee a trozos, y ese es el cambio que se nota.** Antes se pedía el texto
 * entero de una vez: hasta que OpenAI no terminaba de generar los dos mil
 * caracteres, no sonaba nada. Ahora se pide un trozo corto, empieza a sonar, y
 * mientras suena se va generando el siguiente. Cuando termina el primero, el
 * segundo ya está esperando.
 *
 * Devuelve cuál de las dos ha sonado, para poder decirlo en la interfaz en vez
 * de que parezca que la buena suena mal.
 */
export async function leer(texto: string): Promise<"servidor" | "navegador" | "ninguna"> {
  callar();
  const mia = lectura;
  const corte = new AbortController();
  generando = corte;

  try {
    const trozos = trocear(texto);
    let siguiente = api.leerEnVoz(trozos[0]!, corte.signal);

    for (let i = 0; i < trozos.length; i++) {
      const audio = await siguiente;
      // Callar mientras se generaba: lo que llegue ya no es de nadie.
      if (mia !== lectura) return "servidor";
      // Si el primer trozo no sale, se cae a la voz del navegador con el texto
      // entero. Si falla uno de en medio, se para: media respuesta leída y
      // media repetida por otra voz es peor que quedarse a medias.
      if (!audio || audio.size === 0) {
        if (i === 0) throw new Error("sin audio");
        return "servidor";
      }

      // El siguiente se pide ANTES de ponerse a escuchar éste. Es lo que hace
      // que no haya silencio entre trozo y trozo.
      siguiente =
        i + 1 < trozos.length
          ? api.leerEnVoz(trozos[i + 1]!, corte.signal)
          : Promise.resolve(null);

      await reproducir(audio);
      if (mia !== lectura) return "servidor";
    }
    return "servidor";
  } catch {
    /* se prueba con la del navegador */
  }

  if (mia !== lectura) return "ninguna";
  if (typeof speechSynthesis === "undefined") return "ninguna";

  const frase = new SpeechSynthesisUtterance(texto);
  frase.lang = "es-ES";
  frase.rate = 1.15;
  speechSynthesis.speak(frase);
  return "navegador";
}
